import { Dialog, DialogPanel } from "@headlessui/react"
import {
    Coins,
    LayoutDashboard,
    LogOut,
    Menu,
    MonitorSmartphone,
    Server,
    Wallet,
    X,
} from "lucide-react"
import React, { useState } from "react"
import { Link, NavLink } from "react-router-dom"
import logo from "../assets/logo.png"
import { useAuth } from "../contexts/AuthContext"
import { useWallet } from "../hooks/useWallet"
import { cn } from "../utils/cn"
import ThemeToggle from "./ui/ThemeToggle"

interface LayoutProps {
    children: React.ReactNode
    title: string
    disableHeader?: boolean
    disableYScrolling?: boolean
}

const navLinks = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/devices", label: "Dispositivos", icon: MonitorSmartphone },
    { to: "/credits", label: "Carteira", icon: Wallet },
    { to: "/servers", label: "Servidores", icon: Server, soon: true },
]

/** Logo oficial (wordmark branco + play roxo). No tema claro inverte para o texto ficar escuro. */
const Logo: React.FC<{ className?: string }> = ({ className }) => (
    <Link to="/dashboard" className={cn("inline-flex items-center", className)}>
        <img
            src={logo}
            alt="nanoplay"
            className="h-7 w-auto select-none [filter:invert(1)_hue-rotate(180deg)] dark:[filter:none]"
            draggable={false}
        />
    </Link>
)

/** Saldo de créditos, com atalho para a Carteira. */
const CreditsChip: React.FC<{ className?: string; block?: boolean }> = ({
    className,
    block,
}) => {
    const { data, isLoading } = useWallet()
    const balance = data?.balance
    const empty = !isLoading && (balance ?? 0) <= 0
    return (
        <Link
            to="/credits"
            title="Carteira de créditos"
            className={cn(
                "inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold tabular-nums transition-colors",
                block && "w-full justify-between",
                empty
                    ? "border-warning/40 bg-warning-soft text-warning-text hover:border-warning"
                    : "border-border bg-elevated/60 text-text hover:border-border-strong",
                className
            )}
        >
            <span className="inline-flex items-center gap-2">
                <Coins size={16} className={empty ? "" : "text-primary"} />
                {isLoading ? (
                    <span className="inline-block h-3 w-8 animate-pulse rounded bg-elevated" />
                ) : (
                    <>
                        {balance ?? 0}
                        <span className="font-normal text-muted">
                            {balance === 1 ? "crédito" : "créditos"}
                        </span>
                    </>
                )}
            </span>
            {block && (
                <span className="text-xs font-medium text-primary">
                    {empty ? "Comprar" : "Carteira"}
                </span>
            )}
        </Link>
    )
}

/** Conteúdo da barra lateral (desktop fixa e off-canvas no mobile). */
const SidebarContent: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
    const { user, logout } = useAuth()
    return (
        <div className="flex h-full flex-col">
            <div className="flex h-16 shrink-0 items-center px-6">
                <Logo />
            </div>

            <nav className="flex-1 space-y-1 px-3 py-2" aria-label="Principal">
                {navLinks.map(({ to, label, icon: Icon, soon }) => (
                    <NavLink
                        key={to}
                        to={to}
                        onClick={onNavigate}
                        className={({ isActive }) =>
                            cn(
                                "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                                isActive
                                    ? "bg-primary/15 text-primary"
                                    : "text-muted hover:bg-elevated/70 hover:text-text"
                            )
                        }
                    >
                        {({ isActive }) => (
                            <>
                                <Icon
                                    size={18}
                                    className={cn(
                                        "shrink-0 transition-colors",
                                        isActive
                                            ? "text-primary"
                                            : "text-subtle group-hover:text-text"
                                    )}
                                />
                                <span className="flex-1">{label}</span>
                                {soon && (
                                    <span className="rounded-full bg-warning-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-warning-text">
                                        em breve
                                    </span>
                                )}
                            </>
                        )}
                    </NavLink>
                ))}
            </nav>

            <div className="space-y-3 border-t border-border p-3">
                <CreditsChip block />
                <div className="flex items-center gap-2 px-1">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-bold uppercase text-primary">
                        {user?.username?.slice(0, 2) ?? "?"}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm text-text">
                        {user?.username}
                    </span>
                    <ThemeToggle />
                    <button
                        onClick={logout}
                        title="Sair"
                        className="inline-flex items-center justify-center rounded-lg p-2 text-muted transition-colors hover:bg-elevated hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                    >
                        <span className="sr-only">Sair</span>
                        <LogOut size={18} />
                    </button>
                </div>
            </div>
        </div>
    )
}

// Layout das páginas protegidas: sidebar fixa (lg+) / off-canvas (mobile) + conteúdo.
const Layout: React.FC<LayoutProps> = ({
    children,
    title,
    disableYScrolling,
}) => {
    const { isAuthenticated } = useAuth()
    const [mobileOpen, setMobileOpen] = useState(false)

    if (!isAuthenticated) {
        return (
            <div className="flex min-h-screen flex-1 flex-col bg-bg text-text">
                <div className="flex-1 sm:px-6 lg:px-8">
                    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-0">
                        {children}
                    </div>
                </div>
                <footer className="mt-auto w-full border-t border-border bg-surface py-6">
                    <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 md:flex-row">
                        <div className="flex flex-row items-center md:gap-2">
                            <Link
                                to="/privacy-policy"
                                className="rounded px-3 py-1 text-xs text-muted transition-colors hover:text-text"
                            >
                                Política de Privacidade
                            </Link>
                            <Link
                                to="/terms"
                                className="rounded px-3 py-1 text-xs text-muted transition-colors hover:text-text"
                            >
                                Termos de Uso
                            </Link>
                            <Link
                                to="/login"
                                className="rounded px-3 py-1 text-xs text-muted transition-colors hover:text-text"
                            >
                                Área autenticada
                            </Link>
                        </div>
                        <span className="text-center text-xs text-subtle">
                            &copy; {new Date().getFullYear()} Nanoplay. Todos os
                            direitos reservados.
                        </span>
                    </div>
                </footer>
            </div>
        )
    }

    return (
        <div
            className={cn(
                "min-h-screen bg-bg text-text",
                disableYScrolling && "max-h-screen overflow-hidden"
            )}
        >
            {/* Sidebar desktop */}
            <aside className="hidden lg:fixed lg:inset-y-0 lg:z-40 lg:flex lg:w-64 lg:flex-col lg:border-r lg:border-border lg:bg-surface/60">
                <SidebarContent />
            </aside>

            {/* Sidebar mobile (off-canvas) */}
            <Dialog
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                className="relative z-50 lg:hidden"
            >
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />
                <div className="fixed inset-0 flex">
                    <DialogPanel className="relative flex w-72 max-w-[85%] flex-col border-r border-border bg-surface shadow-elevated">
                        <button
                            type="button"
                            onClick={() => setMobileOpen(false)}
                            aria-label="Fechar menu"
                            className="absolute -right-11 top-3 inline-flex items-center justify-center rounded-full bg-surface p-2 text-muted hover:text-text"
                        >
                            <X size={20} />
                        </button>
                        <SidebarContent onNavigate={() => setMobileOpen(false)} />
                    </DialogPanel>
                </div>
            </Dialog>

            {/* Barra superior mobile */}
            <div className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-bg/90 px-4 backdrop-blur lg:hidden">
                <button
                    type="button"
                    onClick={() => setMobileOpen(true)}
                    aria-label="Abrir menu"
                    className="inline-flex items-center justify-center rounded-lg p-2 text-muted hover:bg-elevated hover:text-text"
                >
                    <Menu size={22} />
                </button>
                <Logo />
                <CreditsChip className="ml-auto px-2.5 py-1 text-xs" />
            </div>

            {/* Conteúdo */}
            <div className="lg:pl-64">
                <header className="px-4 pt-6 sm:px-6 lg:px-8">
                    <div className="mx-auto max-w-7xl">
                        <h1 className="text-xl font-bold text-text md:text-3xl">
                            {title}
                        </h1>
                    </div>
                </header>
                <main className="px-4 py-6 sm:px-6 lg:px-8">
                    <div className="mx-auto max-w-7xl">{children}</div>
                </main>
            </div>
        </div>
    )
}

export default Layout
